<template>
  <b-modal
    id="modal-assign-tracking-template-merchant"
    ref="modal"
    v-model="open"
    size="xl"
    no-close-on-backdrop
    no-enforce-focus
    hide-footer
    title="Assign Merchant"
  >
    <b-card :title="templateName">
      <b-row>
        <b-col class="d-flex justify-content-start align-items-center flex-wrap">
          <b-form-group
            label="Merchants"
            class="w-75 mr-1"
          >
            <div class="assign-merchant-dropdown-wrap">
              <v-select
                v-model="merchant_ids"
                class="assign-merchant-select"
                :options="merchants"
                multiple
                :reduce="option => option.id"
                label="name"
                placeholder="Select Merchants"
                deselect-from-dropdown
                :close-on-select="false"
                @input="handleRemovedValue"
              >
                <template #selected-option>
                  <span />
                </template>
                <template v-slot:option="option">
                  <div class="d-flex align-items-center justify-content-between">
                    <span>{{ option.name }}</span>
                    <feather-icon
                      v-if="merchant_ids.includes(option.id)"
                      icon="CheckIcon"
                      size="14"
                      class="text-primary ml-1"
                    />
                  </div>
                </template>
              </v-select>
              <span class="assign-merchant-placeholder">Select Merchants</span>
            </div>
          </b-form-group>
          <b-form-group class="mt-md-2">
            <b-button
              v-ripple.400="'rgba(113, 102, 240, 0.15)'"
              class="custom-button-color"
              style="margin-top:4px;"
              :disabled="merchant_ids.length === 0"
              @click="assignMerchants()"
            >
              Assign
            </b-button>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-table
          striped
          hover
          responsive
          show-empty
          :per-page="perPage"
          :items="rows"
          :fields="fields"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :sort-direction="sortDirection"
        >
          <template #empty>
            <TableDataFetching
              :rows="rows"
              :data-loading="dataLoading"
            />
          </template>
          <template #cell(name)="data">
            <div class="d-flex align-items-center">
              <div>
                <b-avatar
                  size="32"
                  :text="avatarText(data.item.name)"
                  variant="light-primary"
                />
              </div>
              <div>
                <div class="font-weight-bolder ml-1">
                  {{ data.item.name }}{{ isCashMerchant(data.item) ? ' [ C ]' : '' }}
                </div>
                <div class="font-weight-bold ml-1">
                  {{ data.item.ref_no }}
                </div>
                <div class="font-small-2 ml-1">
                  {{ timestampToDateTime(data.item.created_at) }}
                </div>
                <div class="ml-1">
                  <b-badge
                    v-if="isActiveMerchant(data.item)"
                    variant="light-success"
                  >
                    Active
                  </b-badge>
                  <b-badge
                    v-else
                    variant="light-danger"
                  >
                    Inactive
                  </b-badge>
                </div>
              </div>
            </div>
          </template>
          <template #cell(default_address)="data">
            <div v-if="data.value">
              <div class="d-flex align-items-center">
                <div>
                  <div class="ml-1">
                    {{ data.value.address_line_1 }} {{ data.value.address_line_2 }}
                    {{ data.value.address_line_3 }}
                  </div>
                  <div class="font-weight-bolder ml-1">
                    {{ data.value.city ? data.value.city.name : '' }}
                  </div>
                </div>
              </div>
            </div>
            <div v-else>
              <div class="ml-4">
                <span>---</span>
              </div>
            </div>
          </template>
          <template #cell(contact_no)="data">
            <div class="d-flex align-items-center">
              <div>
                <div class="font-weight-bolder ml-1">
                  {{ merchantPhone(data.item) }}
                </div>
              </div>
            </div>
          </template>
          <template #cell(admin_details)="data">
            <div class="d-flex align-items-center">
              <div>
                <div class="font-weight-bolder ml-1">
                  {{ data.item.admin ? `${data.item.admin.first_name} ${data.item.admin.last_name}` : '' }}
                </div>
                <div class="ml-1">
                  {{ data.item.admin ? data.item.admin.email : '' }}
                </div>
              </div>
            </div>
          </template>
        </b-table>
      </b-row>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            class="d-flex align-items-center justify-content-start mb-1 mb-lg-0"
            cols="12"
            lg="3"
          >
            <label class="width-75 mb-0">Per page</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="pageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block ml-1 width-100 mr-2"
            />
          </b-col>
          <b-col
            class="d-flex align-items-center justify-content-start justify-content-lg-end flex-wrap"
            cols="12"
            lg="9"
          >
            <span class="text-muted mr-1 text-nowrap">Showing {{ meta.from || 0 }} to {{ meta.to || 0 }} of {{ meta.total || 0 }} entries</span>
            <b-pagination
              v-model="page"
              :per-page="perPage"
              :total-rows="meta.total ? meta.total : 0"
              class="mb-0 mt-1 mt-sm-0"
              first-number
              last-number
              next-class="next-item"
              prev-class="prev-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </b-modal>
</template>

<script>
import {
  BButton,
  BModal,
  BTable,
  BCol,
  BFormGroup,
  BCard,
  BBadge,
  BRow,
  BAvatar,
  VBTooltip,
  VBModal,
  BPagination,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import { avatarText } from '@core/utils/filter'
import { timestampToDateTime } from '@core/utils/timeUtil'
import vSelect from 'vue-select'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import TableDataFetching from '@/components/TableDataFetching.vue'

const TrackingSettingsRepository = RepositoryFactory.get('trackingSetting')
const MerchantRepository = RepositoryFactory.get('merchant')

function initialState() {
  return {
    avatarText,
    timestampToDateTime,
    open: false,
    id: null,
    templateName: '',
    dataLoading: false,
    meta: {},
    merchant_ids: [],
    merchants: [],
    perPage: 20,
    pageOptions: [10, 20, 50],
    page: 1,
    sortBy: '',
    sortDesc: false,
    sortDirection: 'asc',
    fields: [
      {
        key: 'id', label: 'Ref No', sortable: true, thClass: 'd-none', tdClass: 'd-none',
      },
      {
        key: 'name', label: 'Merchant', sortable: true, thClass: 'text-left',
      },
      {
        key: 'default_address', label: 'Primary Address', sortable: false, thClass: 'text-left',
      },
      {
        key: 'contact_no', label: 'Contact Number', sortable: false, thClass: 'text-left',
      },
      {
        key: 'admin_details', label: 'Admin Details', sortable: false, thClass: 'text-left',
      },
    ],
    rows: [],
    merchantValue: [],
    merchantCount: 0,
    isRemoveConfirmed: null,
  }
}

export default {
  name: 'AssignTrackingTemplateMerchants',
  directives: {
    'b-modal': VBModal,
    'b-tooltip': VBTooltip,
    Ripple,
  },
  components: {
    BButton,
    BModal,
    BAvatar,
    BCol,
    BFormGroup,
    BCard,
    BBadge,
    BRow,
    BTable,
    BPagination,
    TableDataFetching,
    'v-select': vSelect,
  },
  mixins: [SuccessMessage, ErrorMessage],
  data() {
    return initialState()
  },
  watch: {
    page() {
      this.fetchAssignedMerchantList()
    },
    perPage() {
      this.fetchAssignedMerchantList()
    },
  },
  methods: {
    async openModal(item) {
      this.id = item.id
      this.templateName = item.name || ''
      this.merchant_ids = []
      this.merchantValue = []
      this.merchantCount = 0
      this.isRemoveConfirmed = null
      this.rows = []
      this.meta = {}
      this.page = 1
      this.merchants = []
      this.open = true
      await this.$nextTick()
      await this.fetchMerchants()
      await this.fetchAssignedMerchantList(true)
    },
    normalizeMerchants(list) {
      if (!Array.isArray(list)) {
        return []
      }
      return list
        .filter(merchant => merchant && merchant.id != null)
        .map(merchant => ({
          id: Number(merchant.id),
          name: merchant.name,
        }))
    },
    async fetchMerchants() {
      try {
        let list = []
        const cached = localStorage.getItem('merchant_list')
        if (cached) {
          const parsed = JSON.parse(cached)
          list = Array.isArray(parsed) ? parsed : (parsed.data || [])
        } else {
          const { data } = await MerchantRepository.getMerchantListForDropdown()
          list = (data && data.data) ? data.data : []
          localStorage.setItem('merchant_list', JSON.stringify(list))
        }
        this.merchants = this.normalizeMerchants(list)
      } catch (e) {
        this.merchants = []
        this.showErrorMessage('Sorry for the inconvenience! Please try again.')
      }
    },
    async fetchAssignedMerchantList(syncSelection = false) {
      if (!this.id) {
        return
      }
      this.dataLoading = true
      this.rows = []
      try {
        const { data } = await TrackingSettingsRepository.getTrackingPageTemplateAssignedMerchants(
          this.id,
          this.page,
          this.perPage,
        )
        this.rows = Array.isArray(data.data) ? data.data : []
        this.meta = data.meta || {}
        if (syncSelection) {
          const assignedIds = (data.merchant_ids || []).map(id => Number(id))
          this.merchant_ids = assignedIds
          this.merchantValue = assignedIds.slice()
          this.merchantCount = assignedIds.length
          assignedIds.forEach(id => {
            if (!this.merchants.find(option => option.id === id)) {
              const row = this.rows.find(merchant => Number(merchant.id) === id)
              if (row) {
                this.merchants.push({
                  id,
                  name: row.name,
                })
              }
            }
          })
        }
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.dataLoading = false
    },
    async assignMerchants() {
      try {
        await TrackingSettingsRepository.assignTrackingPageTemplateMerchants(this.id, {
          merchant_ids: this.merchant_ids,
        })
        this.showSuccessMessage('Merchants assigned successfully.')
        if (!this.isRemoveConfirmed) {
          this.open = false
          this.merchants = []
        }
        this.isRemoveConfirmed = null
        this.$emit('refresh')
        if (this.open) {
          this.fetchAssignedMerchantList(true)
        }
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    async handleRemovedValue(merchantSet) {
      if (merchantSet.length > this.merchantValue.length) {
        this.merchantValue = merchantSet.map(item => item)
        return
      }
      if (this.merchantCount !== 0) {
        await this.showMsgBox()
      }
      if (this.isRemoveConfirmed === false) {
        this.merchant_ids = this.merchantValue.map(item => item)
      }
      if (this.isRemoveConfirmed === true) {
        this.merchantValue = merchantSet.map(item => item)
        this.assignMerchants()
      }
    },
    async showMsgBox() {
      return this.$swal({
        title: 'Are you sure?',
        text: 'Are you certain about removing the merchant?',
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'Yes, confirm!',
        cancelButtonText: 'No',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
        allowOutsideClick: false,
        backdrop: 'modal-no-backdrop',
      }).then(value => {
        this.isRemoveConfirmed = value.isConfirmed
      })
    },
    isActiveMerchant(item) {
      return item.is_active === 'active' || item.is_active === true || item.is_active === 1
    },
    isCashMerchant(item) {
      return item.setting && item.setting.merchant_type === 'cash'
    },
    merchantPhone(item) {
      if (!item.default_address) {
        return 'N/A'
      }
      if (item.default_address.phone) {
        return item.default_address.phone
      }
      const contact = item.default_address.default_contact_details || item.default_address.defaultContactDetails
      return (contact && contact.phone) ? contact.phone : 'N/A'
    },
  },
}
</script>
<style lang="scss">
@import '../../../@core/scss/vue/libs/vue-select.scss';
.custom-button-color {
  background-color: #4186f5 !important;
  border-color: #4186f5 !important;
}
.assign-merchant-dropdown-wrap {
  position: relative;
}
.assign-merchant-placeholder {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #b9b9c3;
  pointer-events: none;
  z-index: 1;
}
.assign-merchant-dropdown-wrap:focus-within .assign-merchant-placeholder {
  display: none;
}
.assign-merchant-select {
  .vs__dropdown-toggle {
    min-height: 38px;
    overflow: hidden;
  }
  .vs__selected-options {
    flex-wrap: nowrap !important;
    overflow: hidden;
    max-height: 38px;
  }
  .vs__selected {
    display: none !important;
  }
  .vs__search,
  .vs__search:focus {
    margin: 0;
    padding-left: 10px;
    width: 100% !important;
    max-width: 100% !important;
    opacity: 1 !important;
  }
  // vue-select paints its own placeholder on the search input; the wrapper span replaces it
  // so the label stays visible while merchants are selected.
  .vs__search::placeholder {
    color: transparent;
  }
  .vs__placeholder {
    display: none !important;
  }
}
</style>
